import { AnalyticsPanel } from '@/shared/components/ui/AnalyticsPanel';
import { useEffect, useMemo, useState } from 'react';
import { MapPinned, Navigation, Route, CheckCircle2, Clock3, XCircle, RefreshCcw, Plus, Search, Calendar, Layers, BarChart3, Phone, Mail, Building2, User, Radio, FileText, Camera, Sparkles, Clock, TrendingUp } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { toast } from 'react-hot-toast';
import { type SearchableOption } from '@/shared/components/ui/SearchableSelect';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { useUser } from '@/shared/hooks/useAuth';
import { FollowupPanel } from '@/features/activity/components/TimelinePanel';
import { useDeviceLocation } from '@/features/attendance/hooks/useDeviceLocation';
import {
  describeVisitError,
  useUserOptions,
  useVisitContacts,
  useVisitDashboard,
  useVisitList,
  useVisitMutations,
  useVisitPurposes,
} from '../hooks/useVisits';
import type { VisitSummary } from '../types';
import { VisitsAnalyticsCharts } from '../components/VisitsAnalyticsCharts';
import { EnhancedVisitMap } from '../components/EnhancedVisitMap';
import { PlanVisitModal } from '../components/PlanVisitModal';

import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
function dayKeyOf(d: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayKey() {
  return dayKeyOf(new Date());
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function statusBadgeInfo(status: VisitSummary['status']) {
  switch (status) {
    case 'completed':
      return { label: 'Completed', tone: 'success', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'checked_in':
      return { label: 'Checked In', tone: 'primary', bg: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'danger', bg: 'bg-rose-50 text-rose-700 border-rose-200' };
    default:
      return { label: 'Planned', tone: 'warning', bg: 'bg-amber-50 text-amber-700 border-amber-200' };
  }
}

export function VisitsPage() {
  const user = useUser();
  const canCreate = useHasPermission('visits.insert');
  const canUpdate = useHasPermission('visits.update');
  const canCheckIn = useHasPermission('visits.check_in');
  const canCheckOut = useHasPermission('visits.check_out');
  const canDashboard = useHasPermission('visits.dashboard');
  const canConvertEnquiry = useHasPermission('visits.move_to_enquiry');
  const canConvertFollowup = useHasPermission('visits.move_to_followup');

  // Filters & State
  const [scope, setScope] = useState<'mine' | 'team' | 'all'>('mine');
  const [date, setDate] = useState(todayKey());
  // The list (and the "queue" tab, which reads the same data) was always
  // scoped to a single day with no way to see anything else — a visit
  // scheduled for tomorrow looked like it had vanished the moment the toast
  // said "Visit scheduled successfully," because the still-open page kept
  // showing only today. This toggle switches the query to "today onward"
  // instead of one specific day.
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'analytics' | 'queue'>('all');

  // Queries
  const { data: visits = [], isLoading, refetch } = useVisitList(
    showUpcoming
      ? { scope, from: todayKey(), status: statusFilter || undefined }
      : { scope, date, status: statusFilter || undefined }
  );
  // Query 30-day range for rich analytics
  const { data: timeRangeVisits = [] } = useVisitList({ scope });
  const { data: dashboard } = useVisitDashboard(date, scope);
  const { data: contacts = [] } = useVisitContacts();
  const { data: users = [] } = useUserOptions();
  const { data: purposes = [] } = useVisitPurposes();
  const { updateVisit, checkIn, checkOut, cancelVisit, convertToEnquiry, convertToFollowup } = useVisitMutations();
  const location = useDeviceLocation();

  // Detail draft form
  const [detailDraft, setDetailDraft] = useState({ visitReport: '', outcome: '', nextAction: '' });
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  // Options for modal
  const contactOptions: SearchableOption[] = useMemo(
    () =>
      contacts.map((contact) => ({
        value: contact.id,
        label: contact.name,
        description: [contact.company, contact.phone].filter(Boolean).join(' · '),
      })),
    [contacts]
  );

  const assigneeOptions: SearchableOption[] = useMemo(
    () =>
      users
        .filter((entry) => entry.department === user?.department || user?.role === 'superadmin' || user?.role === 'admin')
        .map((entry) => ({
          value: entry.id,
          label: entry.display_name,
          description: [entry.department, entry.role].filter(Boolean).join(' · '),
        })),
    [users, user]
  );

  const purposeOptions: SearchableOption[] = useMemo(
    () => purposes.map((purpose) => ({ value: purpose._id, label: purpose.name })),
    [purposes]
  );

  // Auto select first visit
  useEffect(() => {
    if (!selectedId && visits[0]) setSelectedId(visits[0].id);
    if (selectedId && !visits.some((visit) => visit.id === selectedId) && visits[0]) setSelectedId(visits[0].id);
  }, [selectedId, visits]);

  const selectedVisit = useMemo(() => visits.find((visit) => visit.id === selectedId) ?? null, [selectedId, visits]);

  useEffect(() => {
    if (!selectedVisit) return;
    setDetailDraft({
      visitReport: selectedVisit.visitReport || '',
      outcome: selectedVisit.outcome || '',
      nextAction: selectedVisit.nextAction || '',
    });
  }, [selectedVisit?.id]);

  // Filtered visits by client search
  const filteredVisits = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visits;
    return visits.filter((v) =>
      [
        v.contactSnapshot?.name,
        v.contactSnapshot?.company,
        v.location,
        v.assignedToName,
        v.purposeName,
        v.requirements,
      ].some((val) => (val ?? '').toLowerCase().includes(q))
    );
  }, [visits, searchQuery]);

  // Actions
  async function runCheckIn(visit: VisitSummary) {
    try {
      const coords = await location.captureLocation();
      await checkIn.mutateAsync({ id: visit.id, coordinates: coords });
      toast.success(`Checked in at ${visit.contactSnapshot?.name || visit.location}.`);
      refetch();
    } catch (error) {
      toast.error(describeVisitError(error, 'Check-in failed.'));
    }
  }

  async function runCheckOut(visit: VisitSummary) {
    try {
      const coords = await location.captureLocation();
      await checkOut.mutateAsync({ id: visit.id, coordinates: coords, ...detailDraft });
      toast.success(`Checked out from ${visit.contactSnapshot?.name || visit.location}.`);
      refetch();
    } catch (error) {
      toast.error(describeVisitError(error, 'Check-out failed.'));
    }
  }

  async function saveVisitDraft() {
    if (!selectedVisit) return;
    try {
      await updateVisit.mutateAsync({ id: selectedVisit.id, payload: detailDraft });
      toast.success('Visit notes saved.');
      refetch();
    } catch (error) {
      toast.error(describeVisitError(error, 'Could not save the visit report.'));
    }
  }

  // Metric aggregates
  const metrics = useMemo(() => {
    const total = dashboard?.total ?? visits.length;
    const completed = dashboard?.completed ?? visits.filter((v) => v.status === 'completed').length;
    const checkedIn = dashboard?.checkedIn ?? visits.filter((v) => v.status === 'checked_in').length;
    const planned = dashboard?.planned ?? visits.filter((v) => v.status === 'planned').length;
    const routeKm = dashboard?.routeDistanceKm ?? visits.reduce((acc, v) => acc + (v.distanceKm || 0), 0);
    const converted = (dashboard?.convertedToEnquiry || 0) + (dashboard?.convertedToFollowup || 0);

    return { total, completed, checkedIn, planned, routeKm, converted };
  }, [dashboard, visits]);

  return (
    <div className="mx-auto flex h-full min-h-screen w-full max-w-[1580px] flex-col gap-5 pb-16">
      <PageHeader
        icon={MapPinned}
        eyebrow="Field Sales"
        title="Visit Planner"
        description="Plan client visits, check in on site with GPS, and see how field time turns into pipeline."
        actions={<>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCcw className="h-4 w-4" /> Refresh
          </Button>
          {canCreate && (
            <Button onClick={() => setIsPlanModalOpen(true)}>
              <Plus className="h-4 w-4" /> Schedule Visit
            </Button>
          )}
        </>}
      />

      {/* ── FILTER & TAB TOOLBAR ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-xs">
        {/* Left Filter Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Date Picker */}
          <div className={`flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 ${showUpcoming ? 'bg-slate-100/60 opacity-50' : 'bg-slate-50/70'}`}>
            <Calendar className="h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={date}
              disabled={showUpcoming}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-none disabled:cursor-not-allowed"
            />
          </div>

          {/* Upcoming Toggle — see today onward instead of one specific day */}
          <button
            type="button"
            onClick={() => setShowUpcoming((v) => !v)}
            className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-all ${
              showUpcoming
                ? 'bg-primary border-primary text-white shadow-xs'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Upcoming
          </button>

          {/* Scope Selector */}
          <div className="flex items-center rounded-xl border border-slate-200 bg-slate-100/80 p-1">
            <button
              type="button"
              onClick={() => setScope('mine')}
              className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                scope === 'mine' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              My Visits
            </button>
            <button
              type="button"
              onClick={() => setScope('team')}
              className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                scope === 'team' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Team Visits
            </button>
            <button
              type="button"
              onClick={() => setScope('all')}
              className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                scope === 'all' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              All
            </button>
          </div>

          {/* Status Filter */}
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="planned">Planned Stops</option>
            <option value="checked_in">Checked In (Active)</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Right View Modes */}
        <div className="flex items-center rounded-xl border border-slate-200 bg-white overflow-hidden shadow-xs">
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold border-r border-slate-200 transition-all ${
              activeTab === 'all' ? 'bg-blue-50 text-blue-700 font-bold' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Layers className="h-3.5 w-3.5" /> Command Hub
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('analytics')}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold border-r border-slate-200 transition-all ${
              activeTab === 'analytics' ? 'bg-blue-50 text-blue-700 font-bold' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <BarChart3 className="h-3.5 w-3.5" /> Analytics & Radar
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('queue')}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold transition-all ${
              activeTab === 'queue' ? 'bg-blue-50 text-blue-700 font-bold' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Route className="h-3.5 w-3.5" /> Visit Queue
          </button>
        </div>
      </div>

      <StatGrid cols={3} className="xl:grid-cols-6">
        <StatCard label="Total Visits" value={metrics.total} hint="In current scope" icon={MapPinned} tone="neutral" />
        <StatCard label="Completed" value={metrics.completed} hint={metrics.total > 0 ? `${Math.round((metrics.completed / metrics.total) * 100)}% of visits` : 'No visits yet'} icon={CheckCircle2} tone="success" />
        <StatCard label="In the Field" value={metrics.checkedIn} hint="Currently checked in" icon={Radio} tone="info" />
        <StatCard label="Upcoming" value={metrics.planned} hint="Pending arrival" icon={Clock} tone="warning" />
        <StatCard label="Route Covered" value={`${metrics.routeKm.toFixed(1)} km`} hint="Straight-line distance" icon={Route} tone="primary" />
        <StatCard label="Conversions" value={metrics.converted} hint="Leads & follow-ups" icon={TrendingUp} tone="success" />
      </StatGrid>

      {/* ── ANALYTICS & GPS RADAR SECTION (Visible in 'all' and 'analytics' tabs) ── */}
      {canDashboard && activeTab === 'analytics' && (
        <div className="grid gap-5 xl:grid-cols-[1.25fr,0.75fr]">
          <VisitsAnalyticsCharts visits={visits} dashboard={dashboard} timeRangeVisits={timeRangeVisits} />
          <EnhancedVisitMap markers={dashboard?.markers ?? []} routeDistanceKm={dashboard?.routeDistanceKm ?? metrics.routeKm} />
        </div>
      )}
      {/* Combined view: the visit queue comes first; analytics and the map stay one click away */}
      {canDashboard && activeTab === 'all' && (
        <AnalyticsPanel storageKey="visits" title="Visit analytics & route map" description="Weekly trend, monthly volume, rep leaderboard, purpose split and GPS radar">
          <div className="grid gap-5 xl:grid-cols-[1.25fr,0.75fr]">
            <VisitsAnalyticsCharts visits={visits} dashboard={dashboard} timeRangeVisits={timeRangeVisits} />
            <EnhancedVisitMap markers={dashboard?.markers ?? []} routeDistanceKm={dashboard?.routeDistanceKm ?? metrics.routeKm} />
          </div>
        </AnalyticsPanel>
      )}

      {/* ── EXECUTION & QUEUE SECTION (Visible in 'all' and 'queue' tabs) ── */}
      {(activeTab === 'all' || activeTab === 'queue') && (
        <div className="grid gap-5 xl:grid-cols-[0.95fr,1.05fr]">
          {/* LEFT: VISITS LIST & QUEUE */}
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
            {/* List Header & Search */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3.5">
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  Visits Queue
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                    {filteredVisits.length}
                  </span>
                </h3>
                <p className="text-[11px] text-slate-500">
                  {showUpcoming ? 'All upcoming scheduled visits, today onward' : `Scheduled field visits for ${date}`}
                </p>
              </div>

              {/* Search Box */}
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search customer, location…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-slate-50/70 pl-8 pr-3 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            {/* Visit Cards Scroll Area */}
            <div className="flex flex-col gap-2.5 overflow-y-auto pr-1" style={{ maxHeight: '640px' }}>
              {isLoading && (
                <div className="flex h-36 items-center justify-center text-xs text-slate-400 font-medium">
                  Loading field visits…
                </div>
              )}

              {!isLoading && filteredVisits.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 p-8 text-center">
                  <p className="text-xs font-semibold text-slate-600">No visits found</p>
                  <p className="mt-1 text-[11px] text-slate-400">Try adjusting your date or search filters.</p>
                  {canCreate && (
                    <button
                      type="button"
                      onClick={() => setIsPlanModalOpen(true)}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline"
                    >
                      <Plus className="h-3 w-3" /> Schedule a visit now
                    </button>
                  )}
                </div>
              )}

              {filteredVisits.map((visit) => {
                const badge = statusBadgeInfo(visit.status);
                const isSelected = selectedId === visit.id;

                return (
                  <div
                    key={visit.id}
                    onClick={() => setSelectedId(visit.id)}
                    className={`group relative cursor-pointer rounded-xl border p-4 text-left transition-all duration-150 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50/40 shadow-sm ring-1 ring-blue-500/20'
                        : 'border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-sm'
                    }`}
                  >
                    {/* Top Row: Customer Name & Status */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors truncate">
                          {visit.contactSnapshot?.name || visit.location}
                        </h4>
                        {visit.contactSnapshot?.company && (
                          <p className="text-xs font-medium text-slate-500 truncate flex items-center gap-1 mt-0.5">
                            <Building2 className="h-3 w-3 text-slate-400" />
                            {visit.contactSnapshot.company}
                          </p>
                        )}
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold border ${badge.bg}`}>
                        {badge.label}
                      </span>
                    </div>

                    {/* Middle Row: Location & Time */}
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1 truncate max-w-[200px]">
                        <MapPinned className="h-3 w-3 text-slate-400" /> {visit.location}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-slate-600">
                        <Clock3 className="h-3 w-3 text-slate-400" /> {formatDateTime(visit.plannedAt)}
                      </span>
                    </div>

                    {/* Bottom Row: Tags & Direct Quick Actions */}
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100/80 pt-2.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {visit.purposeName && (
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                            {visit.purposeName}
                          </span>
                        )}
                        {visit.assignedToName && (
                          <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 flex items-center gap-1">
                            <User className="h-2.5 w-2.5" /> {visit.assignedToName}
                          </span>
                        )}
                        {visit.convertedLeadId && (
                          <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200">
                            ✓ Enquiry
                          </span>
                        )}
                        {visit.convertedFollowupId && (
                          <span className="rounded-md bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-700 border border-purple-200">
                            ✓ Followup
                          </span>
                        )}
                      </div>

                      {/* Direct Quick Action Buttons */}
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {canCheckIn && visit.status === 'planned' && (
                          <button
                            type="button"
                            onClick={() => runCheckIn(visit)}
                            disabled={checkIn.isPending || location.isCapturing}
                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-[11px] font-bold text-white shadow-xs hover:bg-primary-dim transition-colors"
                          >
                            <Navigation className="h-3 w-3" /> Check In
                          </button>
                        )}
                        {canCheckOut && visit.status === 'checked_in' && (
                          <button
                            type="button"
                            onClick={() => runCheckOut(visit)}
                            disabled={checkOut.isPending || location.isCapturing}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white shadow-xs hover:bg-emerald-700 transition-colors"
                          >
                            <CheckCircle2 className="h-3 w-3" /> Check Out
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: DETAIL ACTION DESK & WORKFLOW */}
          <div className="flex flex-col gap-4">
            {!selectedVisit ? (
              <div className="flex h-72 flex-col items-center justify-center rounded-2xl border border-slate-200/80 bg-white p-8 text-center shadow-sm">
                <MapPinned className="h-8 w-8 text-slate-300 mb-2" />
                <h4 className="text-sm font-bold text-slate-700">Select a Visit to Work</h4>
                <p className="mt-1 text-xs text-slate-400">
                  Pick any customer visit from the queue to execute check-in, log discussion reports, and convert to CRM deals.
                </p>
              </div>
            ) : (
              <>
                {/* Contact & Status Header */}
                <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-slate-900">
                          {selectedVisit.contactSnapshot?.name || selectedVisit.location}
                        </h3>
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold border ${
                            statusBadgeInfo(selectedVisit.status).bg
                          }`}
                        >
                          {statusBadgeInfo(selectedVisit.status).label}
                        </span>
                      </div>
                      {selectedVisit.contactSnapshot?.company && (
                        <p className="text-xs font-semibold text-slate-600 mt-0.5">
                          {selectedVisit.contactSnapshot.company}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <MapPinned className="h-3.5 w-3.5 text-slate-400" /> {selectedVisit.location}
                      </p>
                    </div>

                    {/* Contact Quick Info Chips */}
                    <div className="flex flex-wrap gap-2 text-xs">
                      {selectedVisit.contactSnapshot?.phone && (
                        <a
                          href={`tel:${selectedVisit.contactSnapshot.phone}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                          <Phone className="h-3 w-3 text-slate-500" /> {selectedVisit.contactSnapshot.phone}
                        </a>
                      )}
                      {selectedVisit.contactSnapshot?.email && (
                        <a
                          href={`mailto:${selectedVisit.contactSnapshot.email}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                          <Mail className="h-3 w-3 text-slate-500" /> {selectedVisit.contactSnapshot.email}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* GPS & Check-In Telemetry Row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 py-3.5 border-b border-slate-100">
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Check In Time</span>
                      <p className="mt-1 text-xs font-semibold text-slate-800">
                        {formatDateTime(selectedVisit.checkInAt)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Check Out Time</span>
                      <p className="mt-1 text-xs font-semibold text-slate-800">
                        {formatDateTime(selectedVisit.checkOutAt)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Geofence Tolerance</span>
                      <p className="mt-1 text-xs font-semibold text-slate-800">
                        {selectedVisit.geofenceToleranceMeters || 250} meters
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2.5">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Checkout Drift</span>
                      <p className="mt-1 text-xs font-semibold text-slate-800">
                        {(selectedVisit.distanceKm || 0).toFixed(3)} km
                      </p>
                    </div>
                  </div>

                  {/* Requirements Note (if present) */}
                  {selectedVisit.requirements && (
                    <div className="rounded-xl bg-amber-50/60 border border-amber-100 p-3 mt-3.5">
                      <p className="text-[11px] font-bold uppercase text-amber-700 flex items-center gap-1">
                        <FileText className="h-3 w-3" /> Agenda & Requirements
                      </p>
                      <p className="mt-1 text-xs text-slate-700">{selectedVisit.requirements}</p>
                    </div>
                  )}

                  {/* Editable Reports & Discussion Outcomes */}
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Visit Summary & Discussion Notes
                      </label>
                      <textarea
                        className="min-h-24 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        value={detailDraft.visitReport}
                        onChange={(e) => setDetailDraft((curr) => ({ ...curr, visitReport: e.target.value }))}
                        placeholder="Log customer reactions, products presented, quotes promised..."
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Meeting Outcome</label>
                        <input
                          type="text"
                          className="h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          value={detailDraft.outcome}
                          onChange={(e) => setDetailDraft((curr) => ({ ...curr, outcome: e.target.value }))}
                          placeholder="e.g. Price approved, Trial sample requested"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Next Action Item</label>
                        <input
                          type="text"
                          className="h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          value={detailDraft.nextAction}
                          onChange={(e) => setDetailDraft((curr) => ({ ...curr, nextAction: e.target.value }))}
                          placeholder="e.g. Send formal quote by Friday"
                        />
                      </div>
                    </div>

                    {/* Factory Visited Photographs with Date & Location (Item 40) */}
                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <Camera className="h-4 w-4 text-indigo-600" />
                          Factory Visit Photographs (Date & GPS Stamped)
                        </label>
                        {isPhotoUploading && (
                          <span className="text-[10px] font-semibold text-indigo-600">Uploading photo...</span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Take or upload photos of client plant, machines, or lubricant storage. Timestamp and live coordinates are automatically captured.
                      </p>

                      <input
                        type="file"
                        id="factory-photo-input"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setIsPhotoUploading(true);
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const { data } = await api.post<{ url: string }>('/api/upload', fd, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                            });
                            if (data?.url) {
                              // Checklist item 40 — a factory photo must carry the place
                              // it was actually taken. `location` is the GPS hook, which
                              // exposes captureLocation(); it has no `.coordinates`, so
                              // that read was always undefined and every photo silently
                              // fell back to the stop's stored position. A refused or
                              // failed GPS fix must not cost the rep the photo.
                              const coords = await location.captureLocation().catch(() => null);
                              const photoEntry = {
                                url: data.url,
                                name: file.name,
                                capturedAt: new Date().toISOString(),
                                location: selectedVisit.location || 'On Site',
                                lat: coords?.lat ?? selectedVisit.coordinates?.lat ?? undefined,
                                lng: coords?.lng ?? selectedVisit.coordinates?.lng ?? undefined,
                              };
                              const existing = Array.isArray(selectedVisit.attachments) ? selectedVisit.attachments : [];
                              const updated = [...existing, photoEntry];
                              await updateVisit.mutateAsync({
                                id: selectedVisit.id,
                                payload: { attachments: updated },
                              });
                              toast.success('Factory photo saved with date and location stamp.');
                              refetch();
                            }
                          } catch {
                            toast.error('Failed to upload factory photo.');
                          } finally {
                            setIsPhotoUploading(false);
                            e.target.value = '';
                          }
                        }}
                      />

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          disabled={isPhotoUploading}
                          onClick={() => document.getElementById('factory-photo-input')?.click()}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs"
                        >
                          <Camera className="h-3.5 w-3.5 text-indigo-600" />
                          Capture / Upload Photo
                        </button>
                      </div>

                      {/* Display Gallery of Uploaded Photos */}
                      {Array.isArray(selectedVisit.attachments) && selectedVisit.attachments.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-2">
                          {selectedVisit.attachments.map((item: any, idx: number) => {
                            const photoUrl = typeof item === 'string' ? item : item?.url;
                            const capturedTime = typeof item === 'object' && item?.capturedAt
                              ? new Date(item.capturedAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })
                              : 'Stamped';
                            const coords = typeof item === 'object' && item?.lat && item?.lng
                              ? `${item.lat.toFixed(4)}, ${item.lng.toFixed(4)}`
                              : null;

                            return (
                              <div
                                key={idx}
                                className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xs"
                              >
                                <a href={photoUrl} target="_blank" rel="noreferrer" className="block aspect-video bg-slate-100 overflow-hidden">
                                  <img
                                    src={photoUrl}
                                    alt={`Factory Visit ${idx + 1}`}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  />
                                </a>
                                <div className="p-2 text-[10px] bg-white border-t border-slate-100 space-y-0.5">
                                  <div className="font-bold text-slate-800 flex items-center justify-between">
                                    <span>Photo #{idx + 1}</span>
                                    <span className="text-slate-400 font-normal">{capturedTime}</span>
                                  </div>
                                  {coords && (
                                    <div className="text-indigo-600 font-mono truncate" title={`GPS: ${coords}`}>
                                      📍 {coords}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Primary Action Button Bar */}
                  <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                    {canUpdate && (
                      <button
                        type="button"
                        onClick={saveVisitDraft}
                        disabled={updateVisit.isPending}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-xs"
                      >
                        <RefreshCcw className="h-3.5 w-3.5 text-slate-400" />
                        Save Notes
                      </button>
                    )}

                    {canCheckIn && selectedVisit.status === 'planned' && (
                      <button
                        type="button"
                        onClick={() => runCheckIn(selectedVisit)}
                        disabled={checkIn.isPending || location.isCapturing}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-white shadow-md  hover:bg-primary-dim transition-all"
                      >
                        <Navigation className="h-3.5 w-3.5" />
                        {location.isCapturing ? 'Locating GPS…' : 'Check In Stop'}
                      </button>
                    )}

                    {canCheckOut && selectedVisit.status === 'checked_in' && (
                      <button
                        type="button"
                        onClick={() => runCheckOut(selectedVisit)}
                        disabled={checkOut.isPending || location.isCapturing}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-emerald-200 hover:bg-emerald-700 transition-all"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {location.isCapturing ? 'Locating GPS…' : 'Check Out Stop'}
                      </button>
                    )}

                    {canConvertEnquiry && !selectedVisit.convertedLeadId && (
                      <button
                        type="button"
                        onClick={() =>
                          convertToEnquiry.mutate(selectedVisit.id, {
                            onSuccess: () => {
                              toast.success('Lead Enquiry created.');
                              refetch();
                            },
                            onError: (error) => toast.error(describeVisitError(error, 'Could not convert to enquiry.')),
                          })
                        }
                        disabled={convertToEnquiry.isPending}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-purple-500 px-3.5 py-2 text-xs font-bold text-white shadow-sm shadow-purple-200 hover:from-purple-600 hover:to-indigo-700 transition-all"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        Convert to Enquiry
                      </button>
                    )}

                    {canConvertFollowup && !selectedVisit.convertedFollowupId && (
                      <button
                        type="button"
                        onClick={() =>
                          convertToFollowup.mutate(
                            {
                              id: selectedVisit.id,
                              followupNote: detailDraft.nextAction || undefined,
                              details: detailDraft.visitReport || undefined,
                            },
                            {
                              onSuccess: () => {
                                toast.success('Followup task scheduled.');
                                refetch();
                              },
                              onError: (error) =>
                                toast.error(describeVisitError(error, 'Could not schedule followup.')),
                            }
                          )
                        }
                        disabled={convertToFollowup.isPending}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-purple-200 bg-purple-50 px-3.5 py-2 text-xs font-bold text-purple-700 hover:bg-purple-100 transition-colors"
                      >
                        <Clock3 className="h-3.5 w-3.5" />
                        Convert to Followup
                      </button>
                    )}

                    {canUpdate &&
                      selectedVisit.status !== 'completed' &&
                      selectedVisit.status !== 'cancelled' && (
                        <button
                          type="button"
                          onClick={() =>
                            cancelVisit.mutate(
                              { id: selectedVisit.id, outcome: detailDraft.outcome },
                              {
                                onSuccess: () => {
                                  toast.success('Visit cancelled.');
                                  refetch();
                                },
                                onError: (error) =>
                                  toast.error(describeVisitError(error, 'Could not cancel visit.')),
                              }
                            )
                          }
                          disabled={cancelVisit.isPending}
                          className="ml-auto inline-flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
                        >
                          <XCircle className="h-3.5 w-3.5" /> Cancel Stop
                        </button>
                      )}
                  </div>
                </div>

                {/* Followup History Panel */}
                <FollowupPanel parentType="visit" parentId={selectedVisit.id} className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm" />
              </>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: SCHEDULE VISIT ── */}
      <PlanVisitModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        defaultDate={date}
        contactOptions={contactOptions}
        assigneeOptions={assigneeOptions}
        purposeOptions={purposeOptions}
        onVisitCreated={(id) => {
          setSelectedId(id);
          // Guarantees the visit just created is actually visible: switching
          // to "Upcoming" means it shows up regardless of which day it was
          // planned for, instead of silently staying hidden behind whatever
          // single day the list happened to be filtered to.
          setShowUpcoming(true);
          refetch();
        }}
      />
    </div>
  );
}
